import * as React from "react";
import { useParams, useLocation } from "react-router";
import { useToggle } from "react-use";
import { ulid } from "ulid";
import clsx from "clsx";
import { FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PromptGeneratorContextProvider } from "@/components/feature/promptGenerator/contextProvider";
import { usePromptGeneratorContext } from "@/components/feature/promptGenerator/hooks";
import { usePromptLibraryContext } from "@/modules/promptLibrary/usePromptLibraryContext";
import { ModifyNewPromptLibraryItemDialog } from "@/modules/promptLibrary/components/modifyNewPromptLibraryItemDialog";
import { useGetMessagesQuery, useSendMessageMutation, useCancelMessageMutation } from "@/api/open-chat";
import { ScrollableChatRef } from "@/components/feature/chat";
import { OpenChatMessage } from "@/types/open-chat";
import { OpenChatWebSocketHandler } from "./components/webSocketHandler";
import { ChatHeader } from "./components/ChatHeader";
import { ChatInput } from "./components/ChatInput";
import { MessageList } from "./components/MessageList";
import { SidePanel } from "./components/SidePanel";
import { CurrentPdfProvider } from "@/contexts/setPdfContext";
import { PanelType } from "./types";

const emptyList: OpenChatMessage[] = [];

export const OpenChatWithParamsView: React.FC = () => (
  <CurrentPdfProvider>
    <PromptGeneratorContextProvider>
      <OpenChatWithParamsContent />
    </PromptGeneratorContextProvider>
  </CurrentPdfProvider>
);

export const OpenChatWithParamsContent: React.FC = () => {
  const { conversationId = "" } = useParams<{ conversationId: string }>();
  const location = useLocation();
  const { data: messages = emptyList, isLoading: isLoadingFirst } = useGetMessagesQuery(conversationId);
  const { createPromptLibraryModalState, closeCreatePromptLibraryModal } = usePromptLibraryContext();
  const [sendMessageMutation, { isLoading }] = useSendMessageMutation();
  const [cancelMessageMutation] = useCancelMessageMutation();
  const [activePanel, setActivePanel] = React.useState<PanelType>(null);
  const [hasHandledInitialMessage, setHasHandledInitialMessage] = React.useState(false);
  const [isWebSearchEnabled, toggleWebSearch] = useToggle(true);
  const messageListRef = React.useRef<ScrollableChatRef>(null);

  const handleSendMessage = React.useCallback(
    async (message: string, { webSearch = isWebSearchEnabled }: { webSearch?: boolean } = {}) => {
      await sendMessageMutation({
        conversationId,
        message,
        messageId: ulid(),
        prompt: "open-chat-style-prompt",
        webSearch,
      }).unwrap();
    },
    [conversationId, isWebSearchEnabled, sendMessageMutation],
  );

  const handleChatInputSend = React.useCallback(
    (message: string) => handleSendMessage(message, { webSearch: isWebSearchEnabled }),
    [handleSendMessage, isWebSearchEnabled],
  );

  const visibleMessages = React.useMemo(
    () => messages.filter(m => !(m.type === "ai" && m.completed && !m.content)),
    [messages],
  );

  const isOpenChatStreaming = React.useMemo(() => {
    const lastAiMessage = [...messages].reverse().find(m => m.type === "ai");
    return lastAiMessage ? !lastAiMessage.completed : false;
  }, [messages]);

  const handleCancelStreaming = React.useCallback(() => {
    cancelMessageMutation({ conversationId });
  }, [cancelMessageMutation, conversationId]);

  const { promptToChat, sendPromptToChat, isSendingToChat, markChatSendComplete } = usePromptGeneratorContext();
  React.useEffect(() => {
    if (promptToChat) {
      handleSendMessage(promptToChat).catch(() => markChatSendComplete());
      sendPromptToChat(null);
      setTimeout(() => messageListRef.current?.scrollToBottom(), 100);
    }
  }, [promptToChat, sendPromptToChat, handleSendMessage, markChatSendComplete]);

  const wasSendingToChat = React.useRef(false);
  React.useEffect(() => {
    if (isSendingToChat && isOpenChatStreaming) {
      wasSendingToChat.current = true;
    } else if (wasSendingToChat.current && !isOpenChatStreaming) {
      wasSendingToChat.current = false;
      markChatSendComplete();
    }
  }, [isSendingToChat, isOpenChatStreaming, markChatSendComplete]);

  React.useEffect(() => {
    if (!isSendingToChat) return;
    const timeout = setTimeout(() => markChatSendComplete(), 30_000);
    return () => clearTimeout(timeout);
  }, [isSendingToChat, markChatSendComplete]);

  React.useEffect(() => () => markChatSendComplete(), [markChatSendComplete]);

  React.useEffect(() => {
    const initialMessage = location.state?.message as string | undefined;

    if (
      !hasHandledInitialMessage &&
      !isLoadingFirst &&
      initialMessage &&
      typeof initialMessage === "string" &&
      initialMessage.trim()
    ) {
      handleSendMessage(initialMessage);
      setHasHandledInitialMessage(true);
      window.history.replaceState({}, "");
    }
  }, [location.state, isLoadingFirst, handleSendMessage, hasHandledInitialMessage]);

  const { promptToApply, subscribe: subscribeToSelectedPrompt, setPromptToApply } = usePromptLibraryContext();
  React.useEffect(() => {
    if (isLoadingFirst || !promptToApply || promptToApply.source !== "open_chat") {
      return;
    }

    handleSendMessage(promptToApply.prompt);
    setPromptToApply(null);
    setTimeout(() => messageListRef.current?.scrollToBottom(), 100);
  }, [promptToApply, isLoadingFirst, handleSendMessage, setPromptToApply]);

  React.useEffect(() => {
    const unsubscribe = subscribeToSelectedPrompt(prompt => {
      handleSendMessage(prompt);
      setTimeout(() => messageListRef.current?.scrollToBottom(), 100);
    });

    return () => unsubscribe();
  }, [subscribeToSelectedPrompt, handleSendMessage]);

  const handlePanelToggle = React.useCallback((panel: Exclude<PanelType, null>) => {
    setActivePanel(prev => (prev === panel ? null : panel));
  }, []);

  const handlePanelClose = React.useCallback(() => {
    setActivePanel(null);
  }, []);

  const onImprovePrompt = React.useCallback(() => {
    setActivePanel("improve");
  }, []);

  const [activeSourceIds, setActiveSourceIds] = React.useState<number[]>([]);
  const [activeMessageId, setActiveMessageId] = React.useState<string | null>(null);

  const onWebCitationClick = React.useCallback((sourceIds: number[], messageId?: string) => {
    setActiveSourceIds(sourceIds);
    setActiveMessageId(messageId ?? null);
    setActivePanel("sources");
  }, []);

  const onSourcesButtonClick = React.useCallback((messageId?: string) => {
    setActiveSourceIds([]);
    setActiveMessageId(messageId ?? null);
    setActivePanel("sources");
  }, []);

  const handleWebSearchToggle = React.useCallback(() => {
    toggleWebSearch();
  }, [toggleWebSearch]);

  const isPanelOpen = activePanel !== null;

  return (
    <>
      <OpenChatWebSocketHandler conversationId={conversationId} />

      <div className="-mx-4 -my-8 flex h-screen overflow-hidden sm:-mx-6 lg:-mx-8">
        <div
          className={clsx("flex w-1/2 flex-col transition-transform duration-500 ease-in-out", {
            "translate-x-0": isPanelOpen,
            "translate-x-1/2": !isPanelOpen,
          })}
        >
          <div className="mx-auto flex h-full w-[90%] max-w-[90%] flex-col pt-2" style={{ maxWidth: 800 }}>
            <ChatHeader
              isPanelOpen={isPanelOpen}
              onPanelToggle={handlePanelToggle}
              conversationId={conversationId}
              messages={visibleMessages}
            />
            <MessageList
              ref={messageListRef}
              messages={visibleMessages}
              isLoading={isLoading}
              isLoadingFirst={isLoadingFirst}
              onImprovePrompt={onImprovePrompt}
              onWebCitationClick={onWebCitationClick}
              onSourcesButtonClick={onSourcesButtonClick}
            />
            <ChatInput
              onSendMessage={handleChatInputSend}
              isStreaming={isOpenChatStreaming}
              isReady={!isLoading && !isLoadingFirst}
              isWebSearchEnabled={isWebSearchEnabled}
              onToggleWebSearch={handleWebSearchToggle}
              onCancel={handleCancelStreaming}
            />
          </div>
        </div>

        <SidePanel
          activePanel={activePanel}
          isPanelOpen={isPanelOpen}
          onPanelChange={setActivePanel}
          onClose={handlePanelClose}
          conversationId={conversationId}
          messages={visibleMessages}
          activeSourceIds={activeSourceIds}
          activeMessageId={activeMessageId}
        />

        {!isPanelOpen && (
          <Button
            variant="outline"
            size="icon"
            className="fixed right-4 top-4 h-9 w-9"
            onClick={() => handlePanelToggle("library")}
            title="Show Panel"
          >
            <FileText size={20} />
          </Button>
        )}
      </div>

      {createPromptLibraryModalState && (
        <ModifyNewPromptLibraryItemDialog
          mode="add"
          isOpen={createPromptLibraryModalState !== null}
          setIsOpen={() => closeCreatePromptLibraryModal()}
          item={{
            prompt: createPromptLibraryModalState.prompt,
            source: createPromptLibraryModalState.source,
          }}
        />
      )}
    </>
  );
};
